import React, { useState } from 'react';
import { IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import AccessControl from '@finpay-development/shared-platform/lib/esm/security/components/access-control';
import { RolePageNames } from '@finpay-development/shared-platform/lib/esm/security/model/role-page-names';
import { DetailedLedger } from '@finpay-development/shared-platform/lib/esm/shared/model/ledger';
import { PaymentStatus } from '@finpay-development/shared-platform/lib/esm/patient/components/models/payment-status';
import { PAYMENT_TYPES } from '@finpay-development/shared-platform/lib/esm/patient/components/models/payment-type';

export type MenuTexts = {
  menuText: string;
  deleteModalTitle: string;
};

export type ResendReceiptTexts = {
  menuText: string;
  refundMenuText: string;
  modalTitle: string;
};

type MenuAction = "refund" | "resend receipt";

type TransactionData = {
  transactionId: number;
  isPaymentTypeCard?: boolean;
  paymentStatus?: string;
  paymentTypeId: number;
  isPaymentCaptured?: boolean;
  isCaptured?: boolean;
  paymentMethodTypeId?: number;
  paymentId?: number;
  paymentReversalId?: number;
  disputeStatus?: string;
  paymentAmt?: number;
};

interface TransactionMenuProps {
  transactionData: TransactionData;
  detailedLedger: DetailedLedger[];
  reversalIdArray: number[];
  reversalPaymentOriginArray: number[];
  disputeIdArray: number[];
  isAccountHolder: boolean;
  workflowId?: number;
  onRefund: (transactionId: number, menuTexts: MenuTexts) => void;
  onResendReceipt: (transactionId: number, isRefund: boolean) => void;
  onNavigateToDispute: (disputeId: number) => void;
  resendReceiptButton: ResendReceiptTexts;
  testId: string;
}

const TransactionMenu: React.FC<TransactionMenuProps> = ({
  transactionData,
  detailedLedger,
  reversalIdArray,
  reversalPaymentOriginArray,
  disputeIdArray,
  isAccountHolder,
  workflowId,
  onRefund,
  onResendReceipt,
  onNavigateToDispute,
  resendReceiptButton,
  testId
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [optionTexts, setOptionTexts] = useState<MenuTexts>({
    menuText: "Refund",
    deleteModalTitle: "Refund",
  });

  const determineMenuTexts = (): MenuTexts => {
    const { 
      paymentTypeId, 
      isPaymentTypeCard, 
      paymentStatus, 
      isPaymentCaptured,
      disputeStatus 
    } = transactionData;
    
    const isPaymentPending = paymentStatus === "Pending";
    const isDisputeLost = disputeStatus === "lost";
    const isDisputeWon = disputeStatus === "won";
    const isDisputePartialWon = disputeStatus === "partial_won";
    const isDisputePending = disputeStatus === "needs_response";
    
    if (isDisputeLost) {
      return { menuText: "Resend Refund Receipt", deleteModalTitle: "Resend Refund Receipt" };
    } else if (isDisputeWon || isDisputePartialWon) {
      return { menuText: "Resend Receipt", deleteModalTitle: "Resend Receipt" };
    } else if (isDisputePending) {
      return { menuText: "Resend Receipt", deleteModalTitle: "Resend Receipt" };
    } else if (paymentTypeId === PAYMENT_TYPES.AT_FACILITY.paymentTypeId) {
      return { menuText: "Remove", deleteModalTitle: "Remove Funds" };
    } else if (!isPaymentCaptured || isPaymentPending) {
      if (!isPaymentTypeCard) {
        return workflowId === 3
          ? { menuText: "Cancel", deleteModalTitle: "Cancel Transfer" }
          : { menuText: "Remove", deleteModalTitle: "Remove Funds" };
      } else {
        return { menuText: "Release", deleteModalTitle: "Release Funds" };
      }
    } else {
      return { menuText: "Refund", deleteModalTitle: "Issue Refund" };
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setOptionTexts(determineMenuTexts());
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (menuAction?: MenuAction) => {
    setAnchorEl(null);
    
    if (menuAction === 'refund') {
      onRefund(transactionData.transactionId, optionTexts);
    } else if (menuAction === 'resend receipt') {
      if (disputeIdArray.includes(transactionData.transactionId) || transactionData.disputeStatus) {
        const isRefund = transactionData.disputeStatus === "lost";
        onResendReceipt(transactionData.transactionId, isRefund);
      } 
      else if (reversalIdArray.includes(transactionData.transactionId)) {
        onResendReceipt(transactionData.transactionId, true);
      }
      else {
        onResendReceipt(transactionData.transactionId, false);
      }
    }
  };

  const findCurrentTransaction = () => {
    return detailedLedger.find(
      (transaction) =>
        transaction.payment?.paymentId === transactionData.transactionId ||
        transaction.reversal?.paymentReversalId === transactionData.transactionId
    );
  };

  const isTransactionSuccessful = () => {
    const currentTransaction = findCurrentTransaction();
    return currentTransaction?.payment?.paymentStatus === PaymentStatus.success && 
      (currentTransaction?.payment?.isCaptured || currentTransaction?.reversal?.paymentReversalId);
  };

  const renderMenuButtons = () => {
    const { transactionId, disputeStatus, paymentTypeId, paymentStatus } = transactionData;
    
    if (paymentTypeId === PAYMENT_TYPES.AT_FACILITY.paymentTypeId && paymentStatus === 'Success') {
      return (
        <AccessControl
          rolePageName={RolePageNames.PatientRecords}
          actionName="Issue Refund"
          renderNoAccess={() => null}
        >
          <MenuItem onClick={() => handleMenuClose('refund')} className="danger">
            {optionTexts.menuText}
          </MenuItem>
        </AccessControl>
      );
    }
    
    if (disputeIdArray.includes(transactionId) || disputeStatus) {      
      if (disputeStatus === "won") {
        return (
          <>
            <MenuItem onClick={() => {
              setAnchorEl(null);
              onResendReceipt(transactionId, false);
            }} className="danger">
              {resendReceiptButton.menuText}
            </MenuItem>
            <MenuItem onClick={() => {
              setAnchorEl(null);
              onNavigateToDispute(transactionId);
            }} className="danger" test-id="won-dispute-button">
              Dispute Response
            </MenuItem>
          </>
        );
      } else if (disputeStatus === "partial_won") {
        return (
          <>
            <MenuItem onClick={() => {
              setAnchorEl(null);
              onResendReceipt(transactionId, false);
            }} className="danger">
              {resendReceiptButton.menuText}
            </MenuItem>
            <MenuItem onClick={() => {
              setAnchorEl(null);
              onNavigateToDispute(transactionId);
            }} className="danger" test-id="partial-won-dispute-button">
              Dispute Response
            </MenuItem>
          </>
        );
      } else if (disputeStatus === "lost") {
        return (
          <>
            <MenuItem onClick={() => {
              setAnchorEl(null);
              onResendReceipt(transactionId, true);
            }} className="danger">
              {resendReceiptButton.refundMenuText}
            </MenuItem>
            <MenuItem onClick={() => {
              setAnchorEl(null);
              onNavigateToDispute(transactionId);
            }} className="danger" test-id="lost-dispute-button">
              Dispute Response
            </MenuItem>
          </>
        );
      } else if (disputeStatus === "needs_response") {
        return (
          <>
            <MenuItem onClick={() => {
              setAnchorEl(null);
              onResendReceipt(transactionId, false);
            }} className="danger">
              {resendReceiptButton.menuText}
            </MenuItem>
            <MenuItem onClick={() => {
              setAnchorEl(null);
              onNavigateToDispute(transactionId);
            }} className="danger" test-id="nr-dispute-button">
              Dispute Response
            </MenuItem>
          </>
        );
      } else if (disputeStatus === "under_review") {
        return (
          <>
            <MenuItem onClick={() => {
              setAnchorEl(null);
              onResendReceipt(transactionId, false);
            }} className="danger">
              {resendReceiptButton.menuText}
            </MenuItem>
            <MenuItem onClick={() => {
              setAnchorEl(null);
              onNavigateToDispute(transactionId);
            }} className="danger" test-id="ur-dispute-button">
              Dispute Response
            </MenuItem>
          </>
        );
      }  else {
        return null;
      }
    }
    
    if (reversalIdArray.includes(transactionId)) {
      return (
        <MenuItem onClick={() => handleMenuClose('resend receipt')} className="danger">
          {resendReceiptButton.refundMenuText}
        </MenuItem>
      );
    }
    
    if (!reversalPaymentOriginArray.includes(transactionId)) {
      const refundMenuItem = (
        <MenuItem onClick={() => handleMenuClose('refund')} className="danger">
          {optionTexts.menuText}
        </MenuItem>
      );

      return (
        <>
          <AccessControl
            rolePageName={RolePageNames.PatientRecords}
            actionName="Issue Refund"
            renderNoAccess={() => null}
          >
            {refundMenuItem}
          </AccessControl>
          {isTransactionSuccessful() && (
            <MenuItem onClick={() => handleMenuClose('resend receipt')} className="danger">
              {resendReceiptButton.menuText}
            </MenuItem>
          )}
        </>
      );
    }
    
    return null;
  };

  if (isAccountHolder) {
    return null;
  }

  return (
    <>
      <IconButton
        aria-haspopup="true"
        onClick={handleMenuClick}
        size="large"
        test-id={testId}
      >
        <MoreHorizIcon/>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => handleMenuClose()}
      >
        {renderMenuButtons()}
      </Menu>
    </>
  );
};

export default TransactionMenu;